import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { API_URI } from "./Outils/Api/FetchData.js";

// ─── 3 Contextes séparés ───
const AuthContext = createContext();
const SitesContext = createContext();
const OperationalContext = createContext();

export function DataProvider({ children }) {
  const [loading, setLoading] = useState(true);

  // Auth
  const [utilisateur, setUtilisateur] = useState(null);

  // Sites & Structure
  const [allUtilisateurs, setAllUtilisateurs] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [allSitesLight, setAllSitesLight] = useState([]);
  const [allEtablissements, setAllEtablissements] = useState([]);
  const [allEmployes, setAllEmployes] = useState([]);
  const [allSecteurs, setAllSecteurs] = useState([]);

  // Operational
  const [allPrestations, setAllPrestations] = useState([]);
  const [allArticlesVC, setAllArticlesVC] = useState([]);
  const [allAchats, setAllAchats] = useState([]);
  const [allRefacturations, setAllRefacturations] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API_URI}api/init`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 401 || res.status === 403) {
          localStorage.clear();
          window.location.href = "/login";
          return;
        }
        if (!res.ok) throw new Error("Init failed");
        return res.json();
      })
      .then((payload) => {
        if (!payload) return;
        setUtilisateur(payload.utilisateur);
        if (payload.allUtilisateurs) setAllUtilisateurs(payload.allUtilisateurs);
        if (payload.allSites) setAllSites(payload.allSites);
        if (payload.allSitesLight) setAllSitesLight(payload.allSitesLight);
        if (payload.allEtablissements) setAllEtablissements(payload.allEtablissements);
        if (payload.allEmployes) setAllEmployes(payload.allEmployes);
        if (payload.allSecteurs) setAllSecteurs(payload.allSecteurs);
        if (payload.allPrestations) setAllPrestations(payload.allPrestations);
        if (payload.allArticlesVC) setAllArticlesVC(payload.allArticlesVC);
        if (payload.initAchats) setAllAchats(payload.initAchats);
        if (payload.initRefacturations) setAllRefacturations(payload.initRefacturations);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  // Mémoïser chaque value pour éviter les re-renders inutiles
  const authValue = useMemo(() => ({
    loading, utilisateur,
  }), [loading, utilisateur]);

  const sitesValue = useMemo(() => ({
    allUtilisateurs, setAllUtilisateurs,
    allSites, setAllSites,
    allSitesLight,
    allEtablissements, setAllEtablissements,
    allEmployes, setAllEmployes,
    allSecteurs, setAllSecteurs,
  }), [allUtilisateurs, allSites, allSitesLight, allEtablissements, allEmployes, allSecteurs]);

  const operationalValue = useMemo(() => ({
    allPrestations, setAllPrestations,
    allArticlesVC, setAllArticlesVC,
    allAchats, setAllAchats,
    allRefacturations, setAllRefacturations,
  }), [allPrestations, allArticlesVC, allAchats, allRefacturations]);

  return (
    <AuthContext.Provider value={authValue}>
      <SitesContext.Provider value={sitesValue}>
        <OperationalContext.Provider value={operationalValue}>
          {children}
        </OperationalContext.Provider>
      </SitesContext.Provider>
    </AuthContext.Provider>
  );
}

// ─── Hooks individuels ───
export function useAuth() { return useContext(AuthContext); }
export function useSites() { return useContext(SitesContext); }
export function useOperational() { return useContext(OperationalContext); }

// ─── Hook legacy (rétrocompatible) ───
// Pour ne pas casser tout le code existant qui utilise useData()
export function useData() {
  const auth = useContext(AuthContext);
  const sites = useContext(SitesContext);
  const operational = useContext(OperationalContext);
  return { ...auth, ...sites, ...operational };
}
